import React, { createContext, FC, ReactNode, useEffect, useMemo } from 'react';
import { createContextualCan, useAbility as useCaslAbility } from '@casl/react';
import * as Sentry from '@sentry/react';
import { useRecoilValue } from 'recoil';
import { ability, buildAbilityFor } from '../helpers';
import { authState, companyState } from '../states';

interface IProps {
  children: string | ReactNode | JSX.Element;
}

export const AbilityContext = createContext(ability);
export const useAbility = () => useCaslAbility(AbilityContext);

export const Can = createContextualCan(AbilityContext.Consumer);

export const AbilityProvider: FC<IProps> = ({ children }) => {
  const company = useRecoilValue(companyState).activeCompany;

  const { user, parsedPermissions } = useRecoilValue(authState);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        email: user.email,
        id: user.id,
        companyId: company?.id,
      });
    }
  }, [user, company]);

  const computedAbility = useMemo(() => {
    return buildAbilityFor(parsedPermissions ?? [], user?.id || 0);
  }, [parsedPermissions, user?.id]);

  return (
    <AbilityContext.Provider value={computedAbility}>
      {children}
    </AbilityContext.Provider>
  );
};
